import { compactResponsiveTree, mapValues } from '../compact';
import { compactBackground, compactBorder } from '../features';
import type { BlockCommonProps } from './schemas';

type ShapeDividerValue = BlockCommonProps['shapeDividers']['top']['desktop'];

/** The divider a block gets when one is switched on without its own. */
export const createShapeDivider = (): Required<ShapeDividerValue> => ({
    isEnabled: true,
    view: '1',
    color: '#50B3FF',
    height: 100,
    repeat: 1,
    horizontalFlip: 'none',
    verticalFlip: 'none',
});

const compactDivider = (value: unknown) => {
    const divider = value as ShapeDividerValue | undefined;

    return divider && !divider.isEnabled && Object.keys(divider).length > 1 ? { isEnabled: false } : value;
};

const compactDividers = (dividers: BlockCommonProps['shapeDividers']) =>
    mapValues(dividers, (responsive) => mapValues(responsive as Record<string, unknown>, compactDivider));

/** `hidden` is three switches read together, not a value with a desktop fallback. */
const NOT_RESPONSIVE = new Set(['hidden']);

/**
 * The props of any block subtype, storing only what says something (see
 * `../compact.ts`): a contour, a background layer or a divider that is off keeps
 * its switch alone, and with `syncWithDesktop` on a breakpoint that only repeats
 * desktop is dropped — the editor writes all three alike then, so they only
 * differ on a block migrated from a page that had per-device leftovers.
 */
export function compactBlockProps<
    T extends { syncWithDesktop?: boolean; border?: object; background?: object; shapeDividers?: object },
>(props: T): T {
    const compacted = mapValues(props, (value, key) => {
        if (key === 'border') return compactBorder(value as never);
        if (key === 'background') return compactBackground(value as never);
        if (key === 'shapeDividers') return compactDividers(value as never);

        return value;
    });

    return (props.syncWithDesktop ? compactResponsiveTree(compacted, NOT_RESPONSIVE) : compacted) as T;
}

type DisplayConditionLike = {
    state?: string | null;
    operator?: string | null;
    conditions?: { conditionType?: unknown }[] | null;
} | null;

/**
 * `null` for a rule that shows the block to everyone — the renderer reads `null`
 * as "always" already. That is a `visible` rule whose every condition is
 * `always` (which passes unconditionally) joined by `and` or `or`. An empty
 * condition list is NOT that: it passes nothing, so the block stays hidden, and
 * the rule is kept.
 */
export function compactDisplayCondition<T extends DisplayConditionLike>(condition: T | undefined): T | null {
    if (!condition) {
        return null;
    }

    const conditions = condition.conditions ?? [];
    const showsAlways =
        (condition.state || 'visible') === 'visible' &&
        (condition.operator === 'and' || condition.operator === 'or') &&
        conditions.length > 0 &&
        conditions.every((item) => item.conditionType === 'always');

    return showsAlways ? null : condition;
}
